import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {OrganizationRole} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {WithoutTypenames} from 'graphql-client/WithoutTypenames';
import GroupSelector from 'parties/GroupSelector';

import {OrganizationSelectorOrganization as GeneratedOrganization} from './__generated/OrganizationSelectorOrganization';
import {OrganizationSelectorQuery} from './__generated/OrganizationSelectorQuery';

export type Organization = WithoutTypenames<GeneratedOrganization>;

export const ORGANIZATION_SELECTOR_ORGANIZATION_FRAGMENT = gql`
  fragment OrganizationSelectorOrganization on OrganizationNode {
    id
    handle
    name
  }
`;

export const ORGANIZATION_SELECTOR_QUERY: DocumentNode = gql`
  query OrganizationSelectorQuery @skipAudit @engComponent(name: IDENTITY) {
    currentTenantOrganizations {
      ...OrganizationSelectorOrganization
    }
    currentUser {
      ... on UserNode {
        id
        organizationRoles {
          organization {
            ...OrganizationSelectorOrganization
          }
          role
        }
      }
      ... on BenchlingAppNode {
        id
        organizationRoles {
          organization {
            ...OrganizationSelectorOrganization
          }
          role
        }
      }
    }
  }
  ${ORGANIZATION_SELECTOR_ORGANIZATION_FRAGMENT}
`;

type PresentationalOrganizationSelectorProps = Props & {
  organizations: Array<Organization>;
};

function PresentationalOrganizationSelector(props: PresentationalOrganizationSelectorProps): JSX.Element {
  return (
    <>
      {props.description && <div className="fieldInput-description">{props.description}</div>}
      <GroupSelector
        className={props.className}
        disabled={props.disabled}
        groups={props.organizations}
        groupType={ModelTypeConstants.ORGANIZATION}
        onSelect={props.onSelectOrganization}
        selectedGroup={props.selectedOrganization}
      />
    </>
  );
}

interface Props {
  className?: string;
  disabled?: boolean;
  description?: string;
  selectedOrganization: Organization | null;
  onSelectOrganization: (organization: Organization) => void;
}

type OrganizationSelectorProps = Props & {
  showOnlyUserOrgs: boolean;
  showOnlyUserAdminOrgs?: boolean;
  autoSelectOnlyOrg?: boolean;
};

export default function OrganizationSelector(props: OrganizationSelectorProps): JSX.Element {
  assert(
    !(!props.showOnlyUserOrgs && props.showOnlyUserAdminOrgs),
    'Cannot use showOnlyUserAdminOrgs if showOnlyUserOrgs set to false'
  );

  function selectOrganizations(
    currentUser: OrganizationSelectorQuery['currentUser'] | null,
    currentTenantOrganizations: OrganizationSelectorQuery['currentTenantOrganizations']
  ): Array<Organization> {
    if (props.showOnlyUserOrgs && currentUser) {
      assert(
        currentUser.__typename !== 'ServiceAccountActorNode',
        `Service principal should not access this page.`
      );
      const orgRoles = props.showOnlyUserAdminOrgs
        ? currentUser.organizationRoles.filter((orgRole) => orgRole.role === OrganizationRole.ADMIN)
        : currentUser.organizationRoles;
      return orgRoles.map((orgRole) => orgRole.organization);
    }

    return currentTenantOrganizations;
  }

  return (
    <BenchlingQuery<OrganizationSelectorQuery>
      query={ORGANIZATION_SELECTOR_QUERY}
      renderLoading={() => <PresentationalOrganizationSelector organizations={[]} {...props} />}
      onCompleted={({currentUser, currentTenantOrganizations}) => {
        if (!props.autoSelectOnlyOrg) {
          return;
        }
        const organizations = selectOrganizations(currentUser, currentTenantOrganizations);
        if (organizations.length === 1) {
          props.onSelectOrganization(organizations[0]);
        }
      }}
    >
      {({currentUser, currentTenantOrganizations}) => {
        const organizations = selectOrganizations(currentUser, currentTenantOrganizations);
        return <PresentationalOrganizationSelector organizations={organizations} {...props} />;
      }}
    </BenchlingQuery>
  );
}
